import { Paper, Typography } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { NonNullableItemState } from "../../redux/state";
import {
  AppBarConfirmButton,
  AppBarSaveButton,
} from "@ofqual/portal-components/components/SecondaryAppBar";
import {
  buildMetaData,
  getLookupTracker,
  modelToJsonPatchDocument,
} from "@ofqual/portal-core/core/utils/modelHelpers";
import { News } from "../../domain/News";
import { archiveItem, updatingItem } from "../../redux/actions";
import { ModuleAppBar } from "../ModuleAppBar";
import { NewsDetails } from "../sections/NewsDetails";
import { SectionProps } from "../sections/SectionProps";
import { authSelector } from "../../redux/selectors";

const useStyles = makeStyles((theme) => ({
  formHeading: {
    color: "#9f9f9f",
    fontSize: "1em",
    marginBottom: "30px",
    fontWeight: 600,
    position: "relative",
  },
  formSection: {
    padding: 30,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
}));

export const ItemDetails: React.FC<{
  state: NonNullableItemState;
}> = ({ state }) => {
  const item = state.item!;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { userType, token } = useSelector(authSelector);

  const disabled = state.status === "UPDATING";

  const {
    handleSubmit,
    control,
    formState,
    errors,
    getValues,
    setValue,
    setError,
    clearErrors,
  } = useForm<News>({
    defaultValues: item,
  });

  // this is important for dirtyFields to be deconstructed/accessed prior to the onSubmit process
  const { dirtyFields } = formState;
  const lookupTracker = useMemo(() => getLookupTracker(), []);

  const onSubmit = (newModel: News) => {
    const fieldsToIgnore: (keyof News)[] = ["image", "files"];

    const changedFields = (Object.keys(dirtyFields) as (keyof News)[]).filter(
      (x) => !fieldsToIgnore.includes(x)
    );

    const newFiles = newModel.files;
    const image = newModel.image[0];
    let document = modelToJsonPatchDocument(newModel, changedFields);
    const resourceTagPatch = document.find(
      (patch) => patch.path === "/resourceTags"
    );
    let resourceTagIds: number[] = item.resourceTags;
    if (resourceTagPatch) {
      resourceTagIds = resourceTagPatch.value;
      document = document.filter((x) => x !== resourceTagPatch);
    }
    const meta = buildMetaData(newModel, item, changedFields, lookupTracker);

    dispatch(
      updatingItem(
        item.id,
        { document, meta },
        resourceTagIds,
        state.attachmentsToDelete,
        newFiles,
        image
      )
    );
  };

  const sectionProps: SectionProps<News> = {
    user: { userType, token },
    disabled,
    control,
    errors,
    formState,
    setValue: (name, value) => setValue(name, value),
    getValues,
    registerCustomError: (name: keyof News, isError: boolean) => {
      const isCurrentlyARegisteredError = Object.keys(errors).includes(name);
      if (isError && !isCurrentlyARegisteredError) {
        setError(name, {});
      }
      if (!isError && isCurrentlyARegisteredError) {
        clearErrors(name);
      }
    },
    lookupTracker,
    item,
  };
  return (
    <>
      <div>
        <ModuleAppBar breadcrumbs={[{ label: `${item.title}` }]}>
          <AppBarConfirmButton
            text={item.archive ? "Unarchive" : "Archive"}
            disabled={disabled}
            onClick={() => dispatch(archiveItem(item.id, !item.archive))}
          >
            <strong>Are you sure?</strong> <br /> <br />
            {item.archive 
              ? "This action will show this article for both AO and Ofqual users"
              : "This action will hide this article from both AO and Ofqual users"
            }
          </AppBarConfirmButton>
          <AppBarSaveButton
            onClick={handleSubmit(onSubmit)}
            isSubmitting={state.status === "UPDATING"}
            isSubmitted={state.lastCrudResult.isSuccess}
            disabled={disabled}
          />
        </ModuleAppBar>

        <form>
          <Typography variant="h6">News details</Typography>

          <Paper className={classes.formSection}>
            <NewsDetails {...sectionProps} />
          </Paper>
        </form>
      </div>
    </>
  );
};
